import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ScrollIndicator from "@/components/ui/ScrollIndicator";
import Button from "@/components/ui/Button";

gsap.registerPlugin(ScrollTrigger);

interface HeroSectionProps {
  heading: string;
  subheading: string;
  cta?: string;
  ctaLink?: string;
  backgroundImage?: string;
}

export default function HeroSection({
  heading,
  subheading,
  cta,
  ctaLink,
  backgroundImage,
}: HeroSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const isMobile = window.innerWidth < 768;

    if (prefersReducedMotion || isMobile || !bgRef.current) return;

    // Set initial transform so the browser composites on a GPU layer immediately,
    // preventing a visual "pop" when ScrollTrigger first applies a transform.
    gsap.set(bgRef.current, { yPercent: 0 });

    gsap.to(bgRef.current, {
      yPercent: 20,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top top",
        end: "bottom top",
        scrub: 0.5,
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === sectionRef.current) st.kill();
      });
    };
  }, []);

  const hasImage = !!backgroundImage;
  const textColor = hasImage ? "text-parchment" : "text-soft-charcoal";
  const subTextColor = hasImage ? "text-parchment/90" : "text-soft-charcoal/80";

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div
        ref={bgRef}
        className="absolute inset-0 -top-[15%] -bottom-[15%] will-change-transform"
        style={
          backgroundImage
            ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: "cover", backgroundPosition: "center" }
            : undefined
        }
      >
        {!backgroundImage && (
          <div className="h-full w-full bg-gradient-to-b from-antique-linen to-parchment" />
        )}
        {backgroundImage ? (
          <div className="absolute inset-0 bg-gradient-to-t from-soft-charcoal/60 via-soft-charcoal/20 to-transparent" />
        ) : (
          <div className="absolute inset-0 bg-soft-charcoal/5" />
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
        <h1
          className={`mb-6 font-serif font-light ${textColor}`}
          style={hasImage ? { textShadow: "0 2px 20px rgba(0,0,0,0.3)" } : undefined}
        >
          {heading}
        </h1>
        <p
          className={`mx-auto mb-10 max-w-2xl font-body text-lg leading-relaxed lg:text-xl ${subTextColor}`}
          style={hasImage ? { textShadow: "0 1px 10px rgba(0,0,0,0.2)" } : undefined}
        >
          {subheading}
        </p>
        {cta && ctaLink && (
          <Button to={ctaLink} variant="outlined" className={hasImage ? "border-fig text-fig bg-parchment/80 hover:bg-parchment hover:text-fig" : ""}>
            {cta}
          </Button>
        )}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <ScrollIndicator />
      </div>
    </section>
  );
}
